import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUp } from 'lucide-react';
import { Link } from 'react-router-dom';

const SpreadsGuide: React.FC = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-[calc(100vh-8rem)] bg-parchment dark:bg-ink">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="max-w-4xl mx-auto"
      >
        <div className="text-center mb-8">
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-ink dark:text-parchment">
            Tarot Spread Layouts
          </h1>
          <p className="mt-6 text-lg text-ink dark:text-parchment max-w-3xl mx-auto">
            Welcome to the magical world of tarot spreads! Whether you're seeking quick clarity or deep insights, each layout offers a unique way to explore your question. Use our pre-built spreads as they are, or let your intuition guide you to adapt them – like transforming a three-card spread from past-present-future into mind-body-spirit. Remember, there's no "right" way – the magic is in making it your own! ✨
          </p>
        </div>

        <div className="space-y-8">
          <div id="single" className="bg-parchment/60 dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-mist dark:border-dusk">
              <h2 className="text-xl font-display font-bold text-sun dark:text-ember">Single Card</h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-ink dark:text-parchment mb-4">
                    The single card draw is the simplest spread, focusing on a specific question or providing daily guidance. It's perfect for beginners or when you need a quick insight.
                  </p>

                  <h3 className="font-medium text-sun dark:text-ember mb-2">When to Use:</h3>
                  <ul className="list-disc pl-5 mb-4 text-ink dark:text-parchment">
                    <li>Daily guidance or reflection</li>
                    <li>Quick answers to simple questions</li>
                    <li>Focused meditation on a single energy or message</li>
                    <li>When you're just beginning to learn tarot</li>
                  </ul>

                  <h3 className="font-medium text-sun dark:text-ember mb-2">Interpretation Tips:</h3>
                  <ul className="list-disc pl-5 text-ink dark:text-parchment">
                    <li>Consider both the card's traditional meaning and your intuitive response</li>
                    <li>Look at all elements of the card's imagery</li>
                    <li>Note your immediate emotional response</li>
                  </ul>
                </div>

                <div className="bg-parchment dark:bg-shadow p-4 rounded-lg border border-vellum dark:border-dusk flex flex-col items-center justify-center">
                  <div className="w-32 h-48 bg-parchment dark:bg-shadow border-2 border-mist dark:border-dusk rounded-lg shadow-md flex items-center justify-center">
                    <span className="text-sun dark:text-ember font-serif text-lg">Card</span>
                  </div>
                  <p className="mt-4 text-sm text-ink/70 dark:text-parchment/60">
                    Position: The card represents the answer to your question or guidance for the day.
                  </p>
                </div>
              </div>
              <button
                onClick={() => scrollToTop()}
                className="mt-6 text-sm text-sun dark:text-ember hover:text-sun dark:hover:text-ember transition-colors"
              >
                Back to top ↑
              </button>
            </div>
          </div>

          <div id="three" className="bg-parchment/60 dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-mist dark:border-dusk">
              <h2 className="text-xl font-display font-bold text-sun dark:text-ember">Three Card Spread</h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-ink dark:text-parchment mb-4">
                    The three card spread is versatile and can be adapted to many different situations. The most common interpretations are past/present/future, mind/body/spirit, or situation/action/outcome.
                  </p>

                  <h3 className="font-medium text-sun dark:text-ember mb-2">Common Interpretations:</h3>
                  <ul className="list-disc pl-5 mb-4 text-ink dark:text-parchment">
                    <li>Past / Present / Future</li>
                    <li>Mind / Body / Spirit</li>
                    <li>Situation / Action / Outcome</li>
                    <li>You / Your Path / Your Potential</li>
                    <li>Strengths / Weaknesses / Advice</li>
                  </ul>

                  <h3 className="font-medium text-sun dark:text-ember mb-2">Interpretation Tips:</h3>
                  <ul className="list-disc pl-5 text-ink dark:text-parchment">
                    <li>Consider how the cards relate to each other</li>
                    <li>Look for patterns or progressions across the three positions</li>
                    <li>Establish your intention for each position before drawing</li>
                  </ul>
                </div>

                <div className="bg-parchment dark:bg-shadow p-4 rounded-lg border border-vellum dark:border-dusk">
                  <div className="flex justify-center space-x-4 mb-4">
                    <div className="w-24 h-36 bg-parchment dark:bg-shadow border-2 border-mist dark:border-dusk rounded-lg shadow-md flex items-center justify-center">
                      <span className="text-sun dark:text-ember font-serif text-sm">Past</span>
                    </div>
                    <div className="w-24 h-36 bg-parchment dark:bg-shadow border-2 border-mist dark:border-dusk rounded-lg shadow-md flex items-center justify-center">
                      <span className="text-sun dark:text-ember font-serif text-sm">Present</span>
                    </div>
                    <div className="w-24 h-36 bg-parchment dark:bg-shadow border-2 border-mist dark:border-dusk rounded-lg shadow-md flex items-center justify-center">
                      <span className="text-sun dark:text-ember font-serif text-sm">Future</span>
                    </div>
                  </div>
                  <p className="text-sm text-ink/70 dark:text-parchment/60 text-center">
                    Left to right: Past influences, present circumstances, and future outcomes.
                  </p>
                </div>
              </div>
              <button
                onClick={() => scrollToTop()}
                className="mt-6 text-sm text-sun dark:text-ember hover:text-sun dark:hover:text-ember transition-colors"
              >
                Back to top ↑
              </button>
            </div>
          </div>

          <div id="five" className="bg-parchment/60 dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-mist dark:border-dusk">
              <h2 className="text-xl font-display font-bold text-sun dark:text-ember">Five Card Spread</h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-ink dark:text-parchment mb-4">
                    The five card spread provides more detail than the three card spread while remaining simpler than the Celtic Cross. It examines the present situation from multiple angles.
                  </p>

                  <h3 className="font-medium text-sun dark:text-ember mb-2">Position Meanings:</h3>
                  <ol className="list-decimal pl-5 mb-4 text-ink dark:text-parchment">
                    <li>Center: The present situation or core issue</li>
                    <li>Above: What crowns you - conscious thoughts or goals</li>
                    <li>Below: What grounds you - subconscious influences</li>
                    <li>Left: Past influences that are fading away</li>
                    <li>Right: Future influences that are coming into play</li>
                  </ol>

                  <h3 className="font-medium text-sun dark:text-ember mb-2">Interpretation Tips:</h3>
                  <ul className="list-disc pl-5 text-ink dark:text-parchment">
                    <li>Start with the center card as the focus</li>
                    <li>Compare opposing positions (above/below, left/right)</li>
                    <li>Look for how the energies flow from past to future</li>
                  </ul>
                </div>

                <div className="bg-parchment dark:bg-shadow p-4 rounded-lg border border-vellum dark:border-dusk flex items-center justify-center">
                  <div className="grid grid-cols-3 grid-rows-3 gap-2">
                    <div className="col-start-2">
                      <div className="w-20 h-28 bg-parchment dark:bg-shadow border-2 border-mist dark:border-dusk rounded-lg shadow-md flex items-center justify-center">
                        <span className="text-sun dark:text-ember font-serif text-xs">Above</span>
                      </div>
                    </div>

                    <div>
                      <div className="w-20 h-28 bg-parchment dark:bg-shadow border-2 border-mist dark:border-dusk rounded-lg shadow-md flex items-center justify-center">
                        <span className="text-sun dark:text-ember font-serif text-xs">Left</span>
                      </div>
                    </div>

                    <div>
                      <div className="w-20 h-28 bg-parchment dark:bg-shadow border-2 border-mist dark:border-dusk rounded-lg shadow-md flex items-center justify-center">
                        <span className="text-sun dark:text-ember font-serif text-xs">Center</span>
                      </div>
                    </div>

                    <div>
                      <div className="w-20 h-28 bg-parchment dark:bg-shadow border-2 border-mist dark:border-dusk rounded-lg shadow-md flex items-center justify-center">
                        <span className="text-sun dark:text-ember font-serif text-xs">Right</span>
                      </div>
                    </div>

                    <div className="col-start-2">
                      <div className="w-20 h-28 bg-parchment dark:bg-shadow border-2 border-mist dark:border-dusk rounded-lg shadow-md flex items-center justify-center">
                        <span className="text-sun dark:text-ember font-serif text-xs">Below</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <button
                onClick={() => scrollToTop()}
                className="mt-6 text-sm text-sun dark:text-ember hover:text-sun dark:hover:text-ember transition-colors"
              >
                Back to top ↑
              </button>
            </div>
          </div>

          <div id="celtic" className="bg-parchment/60 dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-mist dark:border-dusk">
              <h2 className="text-xl font-display font-bold text-sun dark:text-ember">Celtic Cross</h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <p className="text-ink dark:text-parchment mb-4">
                    The Celtic Cross is one of the most comprehensive and widely used tarot spreads. It provides a detailed view of a situation, including the past, present, and future influences, as well as your hopes, fears, and potential outcomes.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                    <div>
                      <h3 className="font-medium text-sun dark:text-ember mb-2">Position Meanings:</h3>
                      <ol className="list-decimal pl-5 text-ink dark:text-parchment space-y-1">
                        <li>Present: The current situation or focus</li>
                        <li>Challenge: Immediate obstacles or challenges</li>
                        <li>Past: Recent events or influences</li>
                        <li>Future: Coming influences in the near future</li>
                        <li>Above: Your aspirations or best outcome</li>
                        <li>Below: Subconscious influences or feelings</li>
                        <li>Advice: Recommended approach or attitude</li>
                        <li>External: How others see you or environment</li>
                        <li>Hopes/Fears: Your emotional state</li>
                        <li>Outcome: The ultimate resolution</li>
                      </ol>
                    </div>

                    <div className="bg-parchment dark:bg-shadow p-4 rounded-lg border border-vellum dark:border-dusk flex items-center justify-center">
                      <div className="grid grid-cols-6 grid-rows-4 gap-1 max-w-xs">
                        <div className="col-start-2 row-start-2">
                          <div className="w-12 h-16 bg-parchment dark:bg-shadow border border-mist dark:border-dusk rounded shadow-sm flex items-center justify-center">
                            <span className="text-sun dark:text-ember font-serif text-xs">1</span>
                          </div>
                        </div>

                        <div className="col-start-2 row-start-2 z-10">
                          <div className="w-12 h-16 bg-parchment dark:bg-shadow border border-mist dark:border-dusk rounded shadow-sm flex items-center justify-center rotate-90">
                            <span className="text-sun dark:text-ember font-serif text-xs">2</span>
                          </div>
                        </div>

                        <div className="col-start-1 row-start-2">
                          <div className="w-12 h-16 bg-parchment dark:bg-shadow border border-mist dark:border-dusk rounded shadow-sm flex items-center justify-center">
                            <span className="text-sun dark:text-ember font-serif text-xs">3</span>
                          </div>
                        </div>

                        <div className="col-start-2 row-start-1">
                          <div className="w-12 h-16 bg-parchment dark:bg-shadow border border-mist dark:border-dusk rounded shadow-sm flex items-center justify-center">
                            <span className="text-sun dark:text-ember font-serif text-xs">5</span>
                          </div>
                        </div>

                        <div className="col-start-3 row-start-2">
                          <div className="w-12 h-16 bg-parchment dark:bg-shadow border border-mist dark:border-dusk rounded shadow-sm flex items-center justify-center">
                            <span className="text-sun dark:text-ember font-serif text-xs">4</span>
                          </div>
                        </div>

                        <div className="col-start-2 row-start-3">
                          <div className="w-12 h-16 bg-parchment dark:bg-shadow border border-mist dark:border-dusk rounded shadow-sm flex items-center justify-center">
                            <span className="text-sun dark:text-ember font-serif text-xs">6</span>
                          </div>
                        </div>

                        <div className="col-start-5 row-start-1">
                          <div className="w-12 h-16 bg-parchment dark:bg-shadow border border-mist dark:border-dusk rounded shadow-sm flex items-center justify-center">
                            <span className="text-sun dark:text-ember font-serif text-xs">10</span>
                          </div>
                        </div>

                        <div className="col-start-5 row-start-2">
                          <div className="w-12 h-16 bg-parchment dark:bg-shadow border border-mist dark:border-dusk rounded shadow-sm flex items-center justify-center">
                            <span className="text-sun dark:text-ember font-serif text-xs">9</span>
                          </div>
                        </div>

                        <div className="col-start-5 row-start-3">
                          <div className="w-12 h-16 bg-parchment dark:bg-shadow border border-mist dark:border-dusk rounded shadow-sm flex items-center justify-center">
                            <span className="text-sun dark:text-ember font-serif text-xs">8</span>
                          </div>
                        </div>

                        <div className="col-start-5 row-start-4">
                          <div className="w-12 h-16 bg-parchment dark:bg-shadow border border-mist dark:border-dusk rounded shadow-sm flex items-center justify-center">
                            <span className="text-sun dark:text-ember font-serif text-xs">7</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <h3 className="font-medium text-sun dark:text-ember mb-2 mt-4">Interpretation Tips:</h3>
                  <ul className="list-disc pl-5 text-ink dark:text-parchment">
                    <li>Start with the center cross (cards 1-6) for the core situation</li>
                    <li>Use the staff (cards 7-10) for deeper insights and outcomes</li>
                    <li>Look at how your environment and others' perceptions affect the situation</li>
                    <li>Consider your hopes and fears in relation to the potential outcome</li>
                    <li>The Celtic Cross tells a complete story from past to future</li>
                  </ul>
                </div>
              </div>
              <button
                onClick={() => scrollToTop()}
                className="mt-6 text-sm text-sun dark:text-ember hover:text-sun dark:hover:text-ember transition-colors"
              >
                Back to top ↑
              </button>
            </div>
          </div>

          <div id="two-options" className="bg-parchment/60 dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-mist dark:border-dusk">
              <h2 className="text-xl font-display font-bold text-sun dark:text-ember">Two Options Spread</h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-ink dark:text-parchment mb-4">
                    The Two Options spread is perfect when you're facing a decision between two paths. This seven-card layout helps you understand the potential outcomes of each choice and provides guidance for making the best decision.
                  </p>

                  <h3 className="font-medium text-sun dark:text-ember mb-2">Position Meanings:</h3>
                  <ol className="list-decimal pl-5 mb-4 text-ink dark:text-parchment space-y-1">
                    <li>You: Your current position and mindset</li>
                    <li>Option A: The first path or choice</li>
                    <li>Option B: The second path or choice</li>
                    <li>Option A Outcome: Likely result of choosing Option A</li>
                    <li>Option B Outcome: Likely result of choosing Option B</li>
                    <li>Hidden Influences: Unseen factors affecting your decision</li>
                    <li>Advice: Guidance for making the best choice</li>
                  </ol>

                  <h3 className="font-medium text-sun dark:text-ember mb-2">When to Use:</h3>
                  <ul className="list-disc pl-5 mb-4 text-ink dark:text-parchment">
                    <li>When facing a major life decision</li>
                    <li>Choosing between job offers or career paths</li>
                    <li>Relationship decisions</li>
                    <li>Any situation with two clear alternatives</li>
                  </ul>

                  <h3 className="font-medium text-sun dark:text-ember mb-2">Interpretation Tips:</h3>
                  <ul className="list-disc pl-5 text-ink dark:text-parchment">
                    <li>Start with your foundation - what grounds you (card 1)</li>
                    <li>Understand your motivation and ideal outcome (cards 2 & 3)</li>
                    <li>Compare what you need to know about each option (cards 4 & 6)</li>
                    <li>Examine the likely outcomes of each path (cards 5 & 7)</li>
                    <li>Consider how your values align with each option's outcome</li>
                  </ul>
                </div>

                <div className="bg-parchment dark:bg-shadow p-4 rounded-lg border border-vellum dark:border-dusk">
                  <div className="grid grid-cols-3 grid-rows-4 gap-2 mb-4 max-w-xs mx-auto">
                    {/* Row 1: Card 3 (Ideal Outcome) */}
                    <div className="col-start-2 row-start-1">
                      <div className="w-16 h-24 bg-parchment dark:bg-shadow border-2 border-mist dark:border-dusk rounded-lg shadow-md flex items-center justify-center">
                        <span className="text-sun dark:text-ember font-serif text-xs">3</span>
                      </div>
                    </div>

                    {/* Row 2: Cards 4, 2, 6 */}
                    <div className="col-start-1 row-start-2">
                      <div className="w-16 h-24 bg-parchment dark:bg-shadow border-2 border-mist dark:border-dusk rounded-lg shadow-md flex items-center justify-center">
                        <span className="text-sun dark:text-ember font-serif text-xs">4</span>
                      </div>
                    </div>
                    <div className="col-start-2 row-start-2">
                      <div className="w-16 h-24 bg-parchment dark:bg-shadow border-2 border-mist dark:border-dusk rounded-lg shadow-md flex items-center justify-center">
                        <span className="text-sun dark:text-ember font-serif text-xs">2</span>
                      </div>
                    </div>
                    <div className="col-start-3 row-start-2">
                      <div className="w-16 h-24 bg-parchment dark:bg-shadow border-2 border-mist dark:border-dusk rounded-lg shadow-md flex items-center justify-center">
                        <span className="text-sun dark:text-ember font-serif text-xs">6</span>
                      </div>
                    </div>

                    {/* Row 3: Cards 5, 1, 7 */}
                    <div className="col-start-1 row-start-3">
                      <div className="w-16 h-24 bg-parchment dark:bg-shadow border-2 border-mist dark:border-dusk rounded-lg shadow-md flex items-center justify-center">
                        <span className="text-sun dark:text-ember font-serif text-xs">5</span>
                      </div>
                    </div>
                    <div className="col-start-2 row-start-3">
                      <div className="w-16 h-24 bg-parchment dark:bg-shadow border-2 border-mist dark:border-dusk rounded-lg shadow-md flex items-center justify-center">
                        <span className="text-sun dark:text-ember font-serif text-xs">1</span>
                      </div>
                    </div>
                    <div className="col-start-3 row-start-3">
                      <div className="w-16 h-24 bg-parchment dark:bg-shadow border-2 border-mist dark:border-dusk rounded-lg shadow-md flex items-center justify-center">
                        <span className="text-sun dark:text-ember font-serif text-xs">7</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-ink/70 dark:text-parchment/60 text-center">
                    Explore your values, motivations, and the outcomes of two different paths.
                  </p>
                </div>
              </div>
              <button
                onClick={() => scrollToTop()}
                className="mt-6 text-sm text-sun dark:text-ember hover:text-sun dark:hover:text-ember transition-colors"
              >
                Back to top ↑
              </button>
            </div>
          </div>

          <div id="relationship" className="bg-parchment/60 dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-mist dark:border-dusk">
              <h2 className="text-xl font-display font-bold text-sun dark:text-ember">Relationship Spread</h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-ink dark:text-parchment mb-4">
                    The Relationship spread explores the dynamics between two people, whether romantic, friendship, family, or professional. This seven-card layout provides insight into both perspectives and the relationship's potential.
                  </p>

                  <h3 className="font-medium text-sun dark:text-ember mb-2">Position Meanings:</h3>
                  <ol className="list-decimal pl-5 mb-4 text-ink dark:text-parchment space-y-1">
                    <li>You: Your role and energy in the relationship</li>
                    <li>Partner: Your partner's role and energy</li>
                    <li>Relationship Dynamic: The current state of the relationship</li>
                    <li>Challenges: Obstacles or issues to address</li>
                    <li>Strengths: What supports and strengthens the bond</li>
                    <li>Potential: Where the relationship could grow</li>
                    <li>Advice: Guidance for nurturing the relationship</li>
                  </ol>

                  <h3 className="font-medium text-sun dark:text-ember mb-2">When to Use:</h3>
                  <ul className="list-disc pl-5 mb-4 text-ink dark:text-parchment">
                    <li>Exploring romantic relationships</li>
                    <li>Understanding family dynamics</li>
                    <li>Improving friendships</li>
                    <li>Professional relationship challenges</li>
                    <li>Any two-person dynamic</li>
                  </ul>

                  <h3 className="font-medium text-sun dark:text-ember mb-2">Interpretation Tips:</h3>
                  <ul className="list-disc pl-5 text-ink dark:text-parchment">
                    <li>Compare the energy of both people (cards 1 & 2)</li>
                    <li>Look at how challenges and strengths balance</li>
                    <li>Consider the relationship's growth potential</li>
                    <li>Focus on what you can control in your role</li>
                    <li>Use the advice to improve the dynamic</li>
                  </ul>
                </div>

                <div className="bg-parchment dark:bg-shadow p-4 rounded-lg border border-vellum dark:border-dusk">
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {/* Top row */}
                    <div>
                      <div className="w-16 h-24 bg-parchment dark:bg-shadow border-2 border-mist dark:border-dusk rounded-lg shadow-md flex items-center justify-center">
                        <span className="text-sun dark:text-ember font-serif text-xs">You</span>
                      </div>
                    </div>
                    <div className="col-start-3">
                      <div className="w-16 h-24 bg-parchment dark:bg-shadow border-2 border-mist dark:border-dusk rounded-lg shadow-md flex items-center justify-center">
                        <span className="text-sun dark:text-ember font-serif text-xs">Partner</span>
                      </div>
                    </div>

                    {/* Second row */}
                    <div className="col-start-2">
                      <div className="w-16 h-24 bg-parchment dark:bg-shadow border-2 border-mist dark:border-dusk rounded-lg shadow-md flex items-center justify-center">
                        <span className="text-sun dark:text-ember font-serif text-xs">Dynamic</span>
                      </div>
                    </div>

                    {/* Third row */}
                    <div>
                      <div className="w-16 h-24 bg-parchment dark:bg-shadow border-2 border-mist dark:border-dusk rounded-lg shadow-md flex items-center justify-center">
                        <span className="text-sun dark:text-ember font-serif text-xs">Challenge</span>
                      </div>
                    </div>
                    <div className="col-start-3">
                      <div className="w-16 h-24 bg-parchment dark:bg-shadow border-2 border-mist dark:border-dusk rounded-lg shadow-md flex items-center justify-center">
                        <span className="text-sun dark:text-ember font-serif text-xs">Strength</span>
                      </div>
                    </div>

                    {/* Fourth row */}
                    <div>
                      <div className="w-16 h-24 bg-parchment dark:bg-shadow border-2 border-mist dark:border-dusk rounded-lg shadow-md flex items-center justify-center">
                        <span className="text-sun dark:text-ember font-serif text-xs">Potential</span>
                      </div>
                    </div>
                    <div className="col-start-2">
                      <div className="w-16 h-24 bg-parchment dark:bg-shadow border-2 border-mist dark:border-dusk rounded-lg shadow-md flex items-center justify-center">
                        <span className="text-sun dark:text-ember font-serif text-xs">Advice</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-ink/70 dark:text-parchment/60 text-center">
                    Explore relationship dynamics and potential for growth.
                  </p>
                </div>
              </div>
              <button
                onClick={() => scrollToTop()}
                className="mt-6 text-sm text-sun dark:text-ember hover:text-sun dark:hover:text-ember transition-colors"
              >
                Back to top ↑
              </button>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center space-x-4">
          <Link
            to="/guide/101"
            className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
          >
            Tarot 101
          </Link>
          <span className="text-mist">•</span>
          <Link
            to="/virtual-deck"
            className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
          >
            Virtual Deck Experience
          </Link>
          <span className="text-mist">•</span>
          <Link
            to="/faq"
            className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
          >
            About This App
          </Link>
        </div>

        {showBackToTop && (
          <button
            onClick={scrollToTop}
            className="fixed bottom-8 right-8 bg-sun text-white p-3 rounded-full shadow-lg hover:bg-sun transition-colors"
            aria-label="Back to top"
          >
            <ArrowUp size={20} />
          </button>
        )}
      </motion.div>
    </div>
  );
};

export default SpreadsGuide;